import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useOpenAddSong } from '../../../../smartLinks/catalog/addSong/useOpenAddSong';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { StudioActionError } from '../../studio/StudioActionError';
import { profileHasReadyFile } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import type { ProfileRouteParams } from '../profileRoute';
import { useProfileData } from '../useProfileData';
import { sendAllMessages } from '../sendAll/sendAllMessages';
import { profileCardModel, type ProfileCardTileKey } from './profileCardModel';
import { profileCardHeadText } from './profileCardText';
import { profileEntryMessages as e } from './profileEntryMessages';
import { profileAvatarSrc, releaseCoverSrc } from './profileThumbSrc';
import { ProfileCardHead } from './ProfileCardHead';
import { ProfileCardStrip } from './ProfileCardStrip';
import { useProfileCardActions } from './useProfileCardActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Profile entry card; each part uses its own slice of it */
import styles from './ProfileEntryCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The tool that a click on a tile opens (design profile-card a, note "Route"). */
const TILE_ROUTE: Readonly<Record<ProfileCardTileKey, ProfileRouteParams>> = {
  spotify: { tool: 'spotifyHeader' },
  youtube: { tool: 'banners', banner: 'youtubeBanner' },
  facebook: { tool: 'banners', banner: 'facebookCover' },
  press: { tool: 'pressPhoto' },
  bio: { tool: 'bio' },
};

/**
 * "Your profile" above the song groups of the Studio picker (design profile-card a, p1–p7): the
 * head with "N of 5 ready", then a strip of real thumbnails at the true aspect of each file, each
 * with its chip and one quick action. It reads the Profile query of the page (no new call) and
 * follows its realtime event. "Send to all" opens the sheet of the Profile (`?sheet=sendAll`).
 */
const StudioProfileEntryCardComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const { data, isError } = useProfileData(accountId);
  const actions = useProfileCardActions(accountId);
  const openAddSong = useOpenAddSong('studio', accountId);
  const model = useMemo(() => (data === undefined ? null : profileCardModel(data)), [data]);
  const { open } = actions;
  const openTile = useCallback((key: ProfileCardTileKey) => open(TILE_ROUTE[key]), [open]);

  const head = useMemo(() => {
    if (model === null || data === undefined) {
      // The query failed: the empty copy with "Open profile"; the page shows its own error and Retry.
      return { title: intl.formatMessage(e.title), sub: isError ? intl.formatMessage(e.subEmpty) : null };
    }
    return profileCardHeadText(intl, model, data.newestRelease);
  }, [data, intl, isError, model]);

  const release = data?.newestRelease ?? null;
  let buttons: React.ReactNode;
  if (model?.mode === 'noSong') {
    buttons = (
      <SMLButton variant="primary" onClick={openAddSong}>
        {intl.formatMessage(m.emptyAddSong)}
      </SMLButton>
    );
  } else if (model?.mode === 'empty' && release !== null) {
    buttons = (
      <>
        <SMLButton
          variant="primary"
          disabled={actions.make.isBusy}
          onClick={() => {
            actions.make.fromRelease(release.catalogItemUuid).catch(console.error);
          }}
        >
          <span data-testid="profile-card-make-set">{intl.formatMessage(m.emptyMake)}</span>
        </SMLButton>
        {!narrow && <SMLButton onClick={() => open()}>{intl.formatMessage(m.emptyPickAnother)}</SMLButton>}
      </>
    );
  } else {
    buttons = (
      <>
        <SMLButton variant="primary" disabled={model === null && !isError} onClick={() => open()}>
          {intl.formatMessage(e.cardOpenProfile)}
        </SMLButton>
        {data !== undefined && (
          <SMLButton onClick={() => open({ sheet: 'sendAll' })}>
            <span data-testid="profile-card-send-all">{intl.formatMessage(sendAllMessages.open)}</span>
          </SMLButton>
        )}
        {!narrow && data !== undefined && profileHasReadyFile(data.assets) && (
          <SMLButton disabled={actions.zip.isBusy} onClick={actions.zip.download}>
            {intl.formatMessage(actions.zip.isBusy ? m.downloadAllBusy : m.downloadAll)}
          </SMLButton>
        )}
      </>
    );
  }

  return (
    <Panel
      as="section"
      tone="brand"
      className={styles.card}
      aria-label={intl.formatMessage(e.cardLabel)}
      data-testid="studio-profile-entry"
      data-mode={isError ? 'error' : (model?.mode ?? 'loading')}
    >
      <div className={styles.glow} aria-hidden="true" />
      <ProfileCardHead
        avatarSrc={data === undefined ? null : profileAvatarSrc(data, 64)}
        title={head.title}
        sub={head.sub}
        ready={model?.ready ?? null}
        total={model?.total ?? 5}
        done={model?.mode === 'done'}
        narrow={narrow}
        buttons={buttons}
      />
      {!isError && (
        <ProfileCardStrip
          model={model}
          ghostSrc={releaseCoverSrc(release, 128)}
          busy={actions.busy}
          copied={actions.copied === 'bioShort'}
          onAction={actions.run}
          onOpenTile={openTile}
        />
      )}
      {narrow && <div className={`${styles.buttons} ${styles.buttonsWide}`}>{buttons}</div>}
      <StudioActionError feedback={actions.make.feedback ?? actions.zip.feedback} />
    </Panel>
  );
};

export const StudioProfileEntryCard = TypedMemo(StudioProfileEntryCardComponent);
